import { RefreshCw, TrendingUp, Activity, ChevronDown, ChevronUp, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { useState } from "react";

// Helper function to format price with dynamic decimal places
const formatPrice = (price) => {
  if (price === null || price === undefined) return '$0.00';
  if (price < 0.0001) {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 10 })}`;
  } else if (price < 1) {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })}`;
  } else if (price < 100) {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  } else {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

const SignalCard = ({ signal, onRefresh, loading, isML = false }) => {
  const [mtfExpanded, setMtfExpanded] = useState(false);
  
  const getSignalColor = () => {
    switch (signal.signal) {
      case "BUY":
        return "#00FF94";
      case "SELL":
        return "#FF0055";
      case "HOLD":
        return "#FFD600";
      default:
        return "#888888";
    }
  };

  const getGlowClass = () => {
    switch (signal.signal) {
      case "BUY":
        return "neon-glow-buy";
      case "SELL":
        return "neon-glow-sell";
      case "HOLD":
        return "neon-glow-hold";
      default:
        return "";
    }
  };

  return (
    <div className={`glass-card rounded-xl p-6 border-white/10 hover:border-primary/50 transition-all duration-300 animate-fade-in-up ${getGlowClass()}`} data-testid={`signal-card-${signal.coin.toLowerCase()}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold heading-font mb-1 flex items-center gap-2" data-testid={`signal-coin-${signal.coin.toLowerCase()}`}>
            {signal.coin}
            {isML && <span className="text-xs px-2 py-1 bg-[#00FF94] text-black rounded-full">ML</span>}
          </h3>
          <div className="text-xs text-muted-foreground">
            {isML ? "AI-Powered ML Signal" : "Technical Analysis Signal"}
          </div>
          {isML && signal.market_regime && (
            <div className="text-xs mt-1">
              <span className="text-muted-foreground">Market: </span>
              <span className={`font-semibold ${
                signal.market_regime.trend === 'bull' ? 'text-[#00FF94]' :
                signal.market_regime.trend === 'bear' ? 'text-[#FF0055]' :
                'text-[#FFD600]'
              }`}>
                {signal.market_regime.trend.toUpperCase()}
              </span>
              <span className="text-muted-foreground"> | Vol: {signal.market_regime.volatility}</span>
            </div>
          )}
          {signal.data_range && (
            <div className="text-xs mt-1 text-muted-foreground">
              <span>Data: </span>
              <span className="text-[#00FF94]">{signal.data_range.start_date}</span>
              <span> to </span>
              <span className="text-[#00FF94]">{signal.data_range.end_date}</span>
              <span> ({signal.data_range.data_points} points, {signal.data_range.period_days} days)</span>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onRefresh}
          disabled={loading}
          className="hover:bg-white/10"
          data-testid={`refresh-signal-${signal.coin.toLowerCase()}`}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-black/30 rounded-lg border border-white/5">
          <div className="text-xs text-muted-foreground mb-1">Signal</div>
          <div 
            className="text-2xl font-bold text-data"
            style={{ color: getSignalColor() }}
            data-testid={`signal-type-${signal.coin.toLowerCase()}`}
          >
            {signal.signal}
          </div>
        </div>
        <div className="text-center p-4 bg-black/30 rounded-lg border border-white/5">
          <div className="text-xs text-muted-foreground mb-1">Confidence</div>
          <div className="text-2xl font-bold text-data" data-testid={`signal-confidence-${signal.coin.toLowerCase()}`}>
            {signal.confidence}%
          </div>
        </div>
        <div className="text-center p-4 bg-black/30 rounded-lg border border-white/5">
          <div className="text-xs text-muted-foreground mb-1">Price</div>
          <div className="text-lg font-bold text-data" data-testid={`signal-price-${signal.coin.toLowerCase()}`}>
            {formatPrice(signal.current_price)}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Analysis Insights
        </h4>
        <div className="space-y-2" data-testid={`signal-reasons-${signal.coin.toLowerCase()}`}>
          {signal.reasons.map((reason, index) => (
            <div key={index} className="text-sm bg-black/20 border border-white/5 rounded-lg p-3">
              <span className="text-white/90">{reason}</span>
            </div>
          ))}
        </div>
      </div>

      {signal.indicators && (
        <div className="mt-6 pt-4 border-t border-white/10">
          <div className="grid grid-cols-2 gap-4 text-xs">
            {signal.indicators.rsi && (
              <div>
                <div className="text-muted-foreground mb-1">RSI</div>
                <div className="text-data font-bold" data-testid={`signal-rsi-${signal.coin.toLowerCase()}`}>{signal.indicators.rsi.toFixed(2)}</div>
              </div>
            )}
            {signal.indicators.macd !== undefined && signal.indicators.macd !== null && (
              <div>
                <div className="text-muted-foreground mb-1">MACD</div>
                <div className="text-data font-bold" data-testid={`signal-macd-${signal.coin.toLowerCase()}`}>
                  {typeof signal.indicators.macd === 'object' 
                    ? (signal.indicators.macd.histogram < 0.0001 && signal.indicators.macd.histogram > -0.0001
                        ? signal.indicators.macd.histogram?.toFixed(10) || '0'
                        : signal.indicators.macd.histogram?.toFixed(6) || '0')
                    : (Math.abs(signal.indicators.macd) < 0.0001
                        ? signal.indicators.macd.toFixed(10)
                        : signal.indicators.macd.toFixed(6))}
                </div>
              </div>
            )}
            {(signal.indicators.sma_20 || signal.indicators.moving_averages?.ma_7) && (
              <div>
                <div className="text-muted-foreground mb-1">SMA (7)</div>
                <div className="text-data font-bold" data-testid={`signal-ma7-${signal.coin.toLowerCase()}`}>
                  {formatPrice(signal.indicators.sma_20 || signal.indicators.moving_averages?.ma_7)}
                </div>
              </div>
            )}
            {(signal.indicators.bollinger_upper || signal.indicators.bollinger_bands?.upper) && (
              <div>
                <div className="text-muted-foreground mb-1">Bollinger</div>
                <div className="text-data font-bold" data-testid={`signal-bollinger-${signal.coin.toLowerCase()}`}>
                  {formatPrice(signal.indicators.bollinger_upper || signal.indicators.bollinger_bands?.upper)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Confidence Breakdown Section - Show full transparency */}
      {signal.confidence_breakdown ? (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-[#FFD600]" />
            <h4 className="text-sm font-semibold text-[#FFD600]">Confidence Breakdown</h4>
          </div>
          
          {/* Breakdown Table */}
          <div className="space-y-2 text-xs">
            {/* Base Confidence */}
            <div className="flex justify-between items-center py-1 border-b border-white/5">
              <span className="text-muted-foreground">📊 Base (AI + Technical)</span>
              <span className="font-semibold text-data">{signal.confidence_breakdown.base_confidence}%</span>
            </div>
            
            {/* Fear & Greed Adjustment */}
            {signal.fear_greed && (
              <div className="flex justify-between items-center py-1 border-b border-white/5">
                <span className="text-muted-foreground">
                  😨 Fear & Greed ({signal.fear_greed.value})
                </span>
                <span className={`font-semibold ${signal.confidence_breakdown.fear_greed_adjustment >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                  {signal.confidence_breakdown.fear_greed_adjustment > 0 ? '+' : ''}{signal.confidence_breakdown.fear_greed_adjustment}%
                </span>
              </div>
            )}
            
            {/* Market Regime Adjustments */}
            {signal.confidence_breakdown.regime_reasons && signal.confidence_breakdown.regime_reasons.length > 0 && (
              signal.confidence_breakdown.regime_reasons.map((reason, idx) => (
                <div key={idx} className="flex justify-between items-center py-1 border-b border-white/5">
                  <span className="text-muted-foreground">
                    📈 {reason[0]}
                  </span>
                  <span className={`font-semibold ${reason[1].startsWith('+') ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                    {reason[1]}
                  </span>
                </div>
              ))
            )}
            
            {/* Soft Filter: Trend Penalty (when counter-trend) */}
            {signal.confidence_breakdown.trend_penalty > 0 && (
              <div className="flex justify-between items-center py-1 border-b border-white/5">
                <span className="text-muted-foreground">
                  📉 Counter-Trend Penalty
                </span>
                <span className="font-semibold text-[#FF0055]">
                  -{signal.confidence_breakdown.trend_penalty.toFixed(1)}%
                </span>
              </div>
            )}
            
            {/* Final Confidence */}
            <div className="flex justify-between items-center py-2 mt-1 bg-white/5 rounded px-2">
              <span className="font-semibold text-white">🎯 Final Confidence</span>
              <span className="font-bold text-lg text-primary">{signal.confidence_breakdown.final_confidence}%</span>
            </div>
          </div>
          
          {/* Fear & Greed Reasoning */}
          {signal.fear_greed && signal.confidence_breakdown.fear_greed_reasoning && (
            <div className="text-xs text-muted-foreground mt-3 italic">
              💡 {signal.confidence_breakdown.fear_greed_reasoning}
            </div>
          )}
          
          {/* Collapsible explanation for missing adjustments */}
          <details className="mt-3">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-white transition-colors">
              ℹ️ Why are some adjustments missing?
            </summary>
            <div className="text-xs text-muted-foreground mt-2 pl-4 space-y-1 border-l border-white/10">
              <p>• <strong>Counter-Trend Penalty:</strong> Only when trading against SMA50/200 trend (scaled 0-15%)</p>
              <p>• <strong>Volatility:</strong> Only in high or low volatility conditions</p>
              <p>• <strong>Trend Strength:</strong> Only when price moved &gt;5% recently</p>
              <p>• <strong>Score Agreement:</strong> Only when AI & Technical scores align (&gt;70%)</p>
            </div>
          </details>
        </div>
      ) : signal.fear_greed ? (
        /* Fallback for old signals without breakdown */
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-[#FFD600]" />
            <h4 className="text-sm font-semibold text-[#FFD600]">Market Sentiment Impact</h4>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-muted-foreground mb-1">Fear & Greed Index</div>
              <div className={`font-bold ${
                signal.fear_greed.value <= 25 ? 'text-[#FF0055]' :
                signal.fear_greed.value <= 45 ? 'text-[#FF8C00]' :
                signal.fear_greed.value <= 55 ? 'text-[#FFD600]' :
                signal.fear_greed.value <= 75 ? 'text-[#00FF94]' :
                'text-[#00FF94]'
              }`}>
                {signal.fear_greed.value} ({signal.fear_greed.classification})
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Confidence Adjustment</div>
              <div className={`font-bold ${signal.fear_greed.adjustment >= 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                {signal.fear_greed.adjustment > 0 ? '+' : ''}{signal.fear_greed.adjustment}%
              </div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground mt-2 italic">
            💡 {signal.fear_greed.reasoning}
          </div>
        </div>
      ) : null}
      
      {/* MTF (Multi-Timeframe) Entry Timing Section - Collapsible */}
      {isML && signal.mtf_confirmation && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <button 
            onClick={() => setMtfExpanded(!mtfExpanded)}
            className="w-full flex items-center justify-between text-left hover:bg-white/5 rounded-lg p-2 -m-2 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#00BFFF]" />
              <h4 className="text-sm font-semibold text-[#00BFFF]">MTF Entry Timing</h4>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                signal.mtf_confirmation.entry_recommendation === 'EXECUTE' ? 'bg-[#00FF94]/20 text-[#00FF94]' :
                signal.mtf_confirmation.entry_recommendation === 'WAIT' ? 'bg-[#FFD600]/20 text-[#FFD600]' :
                signal.mtf_confirmation.entry_recommendation === 'SKIP' ? 'bg-[#FF0055]/20 text-[#FF0055]' :
                'bg-white/10 text-white/70'
              }`}>
                {signal.mtf_confirmation.entry_recommendation}
              </span>
            </div>
            {mtfExpanded ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
          
          {mtfExpanded && (
            <div className="mt-3 space-y-3 animate-fade-in">
              {/* Entry Recommendation */}
              <div className={`p-3 rounded-lg border ${
                signal.mtf_confirmation.entry_recommendation === 'EXECUTE' ? 'bg-[#00FF94]/10 border-[#00FF94]/30' :
                signal.mtf_confirmation.entry_recommendation === 'WAIT' ? 'bg-[#FFD600]/10 border-[#FFD600]/30' :
                signal.mtf_confirmation.entry_recommendation === 'SKIP' ? 'bg-[#FF0055]/10 border-[#FF0055]/30' :
                'bg-white/5 border-white/10'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">
                    {signal.mtf_confirmation.entry_recommendation === 'EXECUTE' ? '🟢' :
                     signal.mtf_confirmation.entry_recommendation === 'WAIT' ? '🟡' :
                     signal.mtf_confirmation.entry_recommendation === 'SKIP' ? '🔴' : '⚪'}
                  </span>
                  <span className="font-semibold text-white">
                    {signal.mtf_confirmation.entry_recommendation === 'EXECUTE' ? 'Good to Enter' :
                     signal.mtf_confirmation.entry_recommendation === 'WAIT' ? 'Wait for Better Entry' :
                     signal.mtf_confirmation.entry_recommendation === 'SKIP' ? 'Skip This Signal' :
                     signal.mtf_confirmation.entry_recommendation}
                  </span>
                </div>
                {signal.mtf_confirmation.wait_reason && (
                  <p className="text-xs text-muted-foreground">
                    {signal.mtf_confirmation.wait_reason}
                  </p>
                )}
                {signal.mtf_confirmation.high_confidence_override && (
                  <p className="text-xs text-[#00FF94] mt-1">
                    ✨ High confidence override applied
                  </p>
                )}
              </div>
              
              {/* Timeframe Details */}
              <div className="grid grid-cols-2 gap-3">
                {/* Hourly Timeframe */}
                <div className="p-3 bg-black/30 rounded-lg border border-white/5">
                  <div className="text-xs text-muted-foreground mb-2 font-semibold">📊 Hourly (48h)</div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Aligned:</span>
                      <span className={signal.mtf_confirmation.hourly_aligned ? 'text-[#00FF94]' : 'text-[#FF0055]'}>
                        {signal.mtf_confirmation.hourly_aligned ? '✓ Yes' : '✗ No'}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Conditions:</span>
                      <span className="text-data">{signal.mtf_confirmation.hourly_conditions_met}/3</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Trend:</span>
                      <span className={`capitalize ${
                        signal.mtf_confirmation.hourly_trend === 'bullish' ? 'text-[#00FF94]' :
                        signal.mtf_confirmation.hourly_trend === 'bearish' ? 'text-[#FF0055]' :
                        'text-[#FFD600]'
                      }`}>
                        {signal.mtf_confirmation.hourly_trend}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* 5-Min Timeframe */}
                <div className="p-3 bg-black/30 rounded-lg border border-white/5">
                  <div className="text-xs text-muted-foreground mb-2 font-semibold">⚡ 5-Min (4h)</div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Momentum:</span>
                      <span className={`capitalize ${
                        signal.mtf_confirmation.fivemin_momentum === 'bullish' ? 'text-[#00FF94]' :
                        signal.mtf_confirmation.fivemin_momentum === 'bearish' ? 'text-[#FF0055]' :
                        signal.mtf_confirmation.fivemin_momentum === 'counter' ? 'text-[#FF8C00]' :
                        'text-[#FFD600]'
                      }`}>
                        {signal.mtf_confirmation.fivemin_momentum}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">RSI:</span>
                      <span className={`${
                        signal.mtf_confirmation.fivemin_rsi >= 70 ? 'text-[#FF0055]' :
                        signal.mtf_confirmation.fivemin_rsi <= 30 ? 'text-[#00FF94]' :
                        'text-data'
                      }`}>
                        {signal.mtf_confirmation.fivemin_rsi?.toFixed(1)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Range:</span>
                      <span className="text-data">{(signal.mtf_confirmation.fivemin_range_position * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* What is MTF? - Help text */}
              <details className="text-xs">
                <summary className="text-muted-foreground cursor-pointer hover:text-white transition-colors">
                  ℹ️ What is MTF (Multi-Timeframe)?
                </summary>
                <div className="mt-2 pl-4 text-muted-foreground space-y-1 border-l border-white/10">
                  <p>MTF checks multiple timeframes before recommending entry:</p>
                  <p>• <strong>EXECUTE:</strong> Hourly aligned + 5-min confirms → Enter now</p>
                  <p>• <strong>WAIT:</strong> Hourly aligned but 5-min shows counter-momentum → Wait for pullback</p>
                  <p>• <strong>SKIP:</strong> Hourly not aligned → Do not enter this signal</p>
                </div>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SignalCard;